import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import type { InboxAttachment, ReplyThread } from '../outreachTypes';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * True when the curator asks for a press photo and the answer has none to link: no ready kit story
 * or feed image of the song AND no profile press photo of the artist. The server folds both into
 * the `pressPhoto` chip (`attachmentLinks.ts`), so the chip is not available only then.
 */
export function pressPhotoIsMissing(thread: Pick<ReplyThread, 'asks'>, attachments: InboxAttachment[]): boolean {
  if (!thread.asks.includes('pressPhoto')) {
    return false;
  }
  const chip = attachments.find((attachment) => attachment.kind === 'pressPhoto');
  return chip !== undefined && !chip.available;
}

interface PressPhotoMissingProps {
  thread: Pick<ReplyThread, 'asks' | 'songTitle' | 'catalogItemUuid'>;
  attachments: InboxAttachment[];
}

/**
 * "No press photo of Forests yet. Make one in Studio" (QA B09): the curator asks for a press
 * photo, but the song has no kit image and the artist has no profile press photo that the answer
 * can link. The link opens the Studio on a feed post of the song; its image becomes the press
 * photo chip.
 */
const PressPhotoMissingComponent: React.FC<PressPhotoMissingProps> = ({ thread, attachments }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  if (!pressPhotoIsMissing(thread, attachments)) {
    return null;
  }
  return (
    <p className={`${shared.small} m-0`} data-testid="press-photo-missing">
      {intl.formatMessage(messages.pressPhotoMissing, { song: thread.songTitle })}{' '}
      <SMLButton
        color="link"
        size="sm"
        omitCap
        className="p-0 align-baseline"
        onClick={() =>
          router.push(SMLRoutes.promoteStudio, { catalogItemUuid: thread.catalogItemUuid, theme: 'feedPost' })
        }
        testID="press-photo-make"
      >
        {intl.formatMessage(messages.pressPhotoMake)}
      </SMLButton>
    </p>
  );
};

export const PressPhotoMissing = TypedMemo(PressPhotoMissingComponent);
